import { Controller } from '@hotwired/stimulus';

import { MODAL_CLOSE, MODAL_OPEN } from '../js/events';


export default class ModalController extends Controller {
    static values = {
        id: String,

        isOpen:            { type: Boolean, default: false },
        clickToClose:      { type: Boolean, default: false },
        disableEscape:     { type: Boolean, default: true },
        preventOverscroll: { type: Boolean, default: true },
        appendToBodyEnd:   { type: Boolean, default: true },
        closeTimeout:      { type: Number, default: 300 },

        url:             { type: String, default: '' },
        handleRedirects: { type: Boolean, default: false },
    };

    static targets = [
        'wrapper',
        'loading',
        'dialog',
        'body',
        'container',
        'content',
    ];

    static classes = [
        'hidden', 'fade', 'bodyShow', 'bodyHide', 'redirecting'
    ];

    _isMovedInDom = false;

    get isOpen() {
        return this.isOpenValue;
    }

    /**
     * @returns {Boolean}
     */
    set isOpen(isOpen) {
        this.isOpenValue = isOpen;
    }

    /**
     * @returns {Boolean}
     */
    get clickToClose() {
        return this.clickToCloseValue;
    }

    /**
     * @returns {Boolean}
     */
    get disableEscape() {
        return this.disableEscapeValue;
    }

    /**
     * @returns {Boolean}
     */
    get preventOverscroll() {
        return this.preventOverscrollValue;
    }

    /**
     * @returns {Boolean}
     */
    get appendToBodyEnd() {
        return this.appendToBodyEndValue;
    }

    get closeTimeout() {
        return this.closeTimeoutValue;
    }

    get url() {
        return this.urlValue;
    }

    get handleRedirects() {
        return this.handleRedirectsValue;
    }


    /**
     * @returns {HTMLElement}
     */
    get wrapper() {
        return this.wrapperTarget;
    }

    /**
     * @returns {HTMLElement}
     */
    get loading() {
        return this.loadingTarget;
    }

    /**
     * @returns {HTMLElement}
     */
    get dialog() {
        return this.dialogTarget;
    }

    /**
     * @returns {HTMLElement}
     */
    get body() {
        return this.bodyTarget;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get content() {
        return this.hasContentTarget ? this.contentTarget : null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get container() {
        return this.hasContainerTarget ? this.containerTarget : null;
    }


    get isVisible() {
        return !this.wrapper.classList.contains(this.hiddenClass);
    }

    set isVisible(visible) {
        if (visible) {
            this.wrapper.classList.remove(this.hiddenClass);
        } else {
            this.wrapper.classList.add(this.hiddenClass);
        }
    }


    connect() {
        window.addEventListener(MODAL_OPEN, this.onOpenEvent);
        window.addEventListener(MODAL_CLOSE, this.onCloseAllEvent);
        this.element.addEventListener(MODAL_CLOSE, this.onCloseThisEvent);

        if (!this.disableEscape) {
            window.addEventListener('keyup', this.handleEscape);
        }

        document.addEventListener('turbo:before-cache', this.turboCacheListener);
    }

    disconnect() {
        window.removeEventListener(MODAL_OPEN, this.onOpenEvent);
        window.removeEventListener(MODAL_CLOSE, this.onCloseAllEvent);
        this.element.removeEventListener(MODAL_CLOSE, this.onCloseThisEvent);

        if (!this.disableEscape) {
            window.removeEventListener('keyup', this.handleEscape);
        }

        document.removeEventListener('turbo:before-cache', this.turboCacheListener);
    }

    clickOutside(event) {
        if (this.clickToClose && this.isOpen) {
            event.preventDefault();
        }
    }

    onOpenEvent = (event) => {
        if (event.detail.modalId === this.idValue) {
            this.open();
        }
    };

    /**
     * Event thrown if all modals have to be closed.
     *
     * @param {Event} event
     */
    onCloseAllEvent = (event) => {
        if (!event.detail.modalId) {
            this.close();
        }
    };

    /**
     * Event thrown if this current modal has to be closed.
     *
     * @param {Event} event
     */
    onCloseThisEvent = (event) => {
        if (event.detail.modalId === this.idValue) {
            event.stopImmediatePropagation();
            this.close();
        }
    };

    open = (event) => {
        this.isOpen = true;
    };

    close = (event) => {
        this.isOpen = false;
    };

    handleEscape = (event) => {
        if (event.key === 'Escape') {
            this.close(event);
        }
    };


    async isOpenValueChanged() {
        if (this.isOpen && !this.isVisible) {
            const shown = await this._show();

            if (shown && this.clickToClose) {
                this.wrapper.addEventListener('click', this._onClickWrapper);
            }

        } else if (!this.isOpen && this.isVisible) {
            this.wrapper.removeEventListener('click', this._onClickWrapper);
            await this._hide();
        }
    }

    async _show() {
        this._showModal(true);
        let show = true;

        this._ensureDomPosition();

        if (this.url) {
            show = await this._loadContent();

        } else if (this.content) {
            await this._setContent(this.content);
            show = true;
        }

        this._showBody(show);

        if (show) {
            this.dialog.firstElementChild.scrollIntoView();
        }

        return show;
    }

    async _hide() {
        this._showBody(false);
        this._showModal(false);
    }

    _onClickWrapper = (event) => {
        if (event.target === this.dialog) {
            this.close();
        }
    }


    /**
     * @returns {Promise<boolean>}
     * @private
     */
    async _loadContent() {
        try {
            const response = await fetch(this.url, {
                method:  'GET',
                headers: {
                    'Accept':           'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (this.handleRedirects && response.redirected) {
                document.body.classList.add(...this.redirectingClasses);
                window.location.href = response.url;

                return false;
            }

            this._setContent(await response.text());

            return true;

        } catch (e) {
            this.open = false;

            return false;
        }
    }


    /**
     * @param {String|HTMLTemplateElement|HTMLElement} content
     * @returns {Promise<HTMLElement>|null}
     */
    async _setContent(content) {
        const container = this.container;

        if (!container) {
            return;
        }

        let promise = null;

        if (typeof content === 'string') {
            container.innerHTML = content;

        } else {
            container.innerHTML = '';

            let contentEl;
            let turboFrame;

            if (content instanceof HTMLTemplateElement) {
                contentEl  = content.content.cloneNode(true);
                turboFrame = this._findTurboFrameToLoad(content.content);
            } else if (content instanceof HTMLElement) {
                contentEl  = content;
                turboFrame = this._findTurboFrameToLoad(contentEl);
            }

            if (turboFrame) {
                promise = this._waitForTurboFrameLoad();
            }

            if (contentEl) {
                container.append(contentEl);
            }
        }

        for (const child of container.children) {
            child.dataset.turboTemporary = 'true';
        }

        return promise;
    }

    /**
     * @returns {Promise<HTMLElement>}
     */
    _waitForTurboFrameLoad() {
        return new Promise((resolve) => {
            const handleEvent = (event) => {
                this.container.removeEventListener('turbo:frame-load', handleEvent);

                resolve(event.target);
            };

            this.container.addEventListener('turbo:frame-load', handleEvent);
        });
    }

    /**
     * @param {HTMLElement|DocumentFragment} element
     * @returns {HTMLElement|DocumentFragment|null}
     */
    _findTurboFrameToLoad(element) {
        for (const child of element.children) {
            if (child.nodeName === 'TURBO-FRAME'
                && child.getAttribute('src')
                && !child.getAttribute('complete')
                && !child.complete
            ) {
                return child;
            }
        }

        return null;
    }


    _showModal(show) {
        if (show) {
            this.isVisible = true;
            this.body.setAttribute('modal-visible', 'true');
            this.loading.classList.remove(this.hiddenClass);

            setTimeout(() => {
                this.wrapper.classList.remove(...this.fadeClasses);
            }, 30);

        } else {
            this.wrapper.classList.add(...this.fadeClasses);

            setTimeout(() => {
                this.isVisible = false;
                this.body.removeAttribute('modal-visible');

                // always remove everything from the DOM (e.g. forms, elements with stimulus controllers)
                this._setContent('');
            }, this.closeTimeout);
        }
    }

    _showBody(show) {
        this._handleOverscroll(show);

        if (show) {
            this.loading.classList.add(this.hiddenClass);

            setTimeout(() => {
                this.body.classList.add(...this.bodyShowClasses);
                this.body.classList.remove(...this.bodyHideClasses);
            }, 1);

        } else {
            this.body.classList.add(...this.bodyHideClasses);
            this.body.classList.remove(...this.bodyShowClasses);
        }
    }

    _handleOverscroll(prevent) {
        if (!this.preventOverscroll) {
            return;
        }

        if (prevent) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

            if (scrollbarWidth > 0) {
                document.documentElement.style.marginRight = `${scrollbarWidth}px`;
            }
            document.body.style.overflowY = 'hidden';

        } else {
            document.documentElement.style.marginRight = null;
            document.body.style.overflowY   = null;
        }
    }

    _ensureDomPosition() {
        if (this._isMovedInDom || !this.appendToBodyEnd) {
            return;
        }

        this.element.dataset.turboTemporary = 'true';

        document.body.append(this.element);
        this._isMovedInDom = true;
    }

    turboCacheListener = () => {
        this.close();
    };
}
